'use client';

import { LayoutGrid, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// ---------------------------------------------------------------------------
// HeroBriefingPreview — static decorative mini-preview for the hero section
// Purely visual, no interactivity, no data fetching (PROJ-29)
// ---------------------------------------------------------------------------

export function HeroBriefingPreview() {
  return (
    <>
      {/* ----------------------------------------------------------------- */}
      {/* Mobile / Tablet variant (< lg) — simplified, no mini-table/tabs   */}
      {/* ----------------------------------------------------------------- */}
      <div className="block lg:hidden w-full rounded-xl border border-border bg-card overflow-hidden card-shadow-lg select-none">
        {/* Compact header bar */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-3 bg-muted/30 min-w-0">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground text-xs font-bold flex-shrink-0">
            SB
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              Muster Drive Systems GmbH
            </p>
            <p className="text-xs text-muted-foreground">
              Erstellt: heute
            </p>
          </div>
        </div>

        {/* 2 Section cards only */}
        <div className="p-4 space-y-3">
          {/* Card 1 — Unternehmensprofil snippet */}
          <div className="rounded-lg border border-border p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-bold flex-shrink-0">
                2
              </span>
              <span className="text-sm font-semibold text-foreground">
                Unternehmensprofil
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mb-2">
              Automotive Tier-1-Zulieferer, gegr. 1987 in Böblingen. Spezialisiert auf Brems- und Antriebssteuergeräte.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-md bg-muted/50 px-2 py-1.5">
                <p className="text-xs text-muted-foreground">Branche</p>
                <p className="text-xs font-medium text-foreground">Automotive Tier-1</p>
              </div>
              <div className="rounded-md bg-muted/50 px-2 py-1.5">
                <p className="text-xs text-muted-foreground">Mitarbeiter</p>
                <p className="text-xs font-medium text-foreground">ca. 387</p>
              </div>
            </div>
          </div>

          {/* Card 2 — Risiko-Recherche snippet (highlighted) */}
          <div className="rounded-lg border border-accent/30 bg-accent/5 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-bold flex-shrink-0">
                7
              </span>
              <span className="text-sm font-semibold text-foreground">
                Risiko-Recherche
              </span>
              <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 ml-auto">
                16 Punkte
              </Badge>
            </div>
            <ul className="space-y-1 text-xs text-muted-foreground">
              <li className="flex items-center gap-1.5">
                <span className="text-red-600 dark:text-red-400 flex-shrink-0">🔴</span>
                <span>Qualitätseskalation — Kritisch</span>
              </li>
              <li className="flex items-center gap-1.5">
                <span className="text-red-600 dark:text-red-400 flex-shrink-0">🔴</span>
                <span>Cyberangriff — Kritisch</span>
              </li>
              <li className="flex items-center gap-1.5">
                <span className="text-amber-600 dark:text-amber-400 flex-shrink-0">🟠</span>
                <span>OEM-Konzentration — Erhöht</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Fade-out gradient suggesting more content */}
        <div className="h-8 bg-gradient-to-t from-card to-transparent -mt-4 relative z-10" />
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Desktop variant (lg+) — full preview, unchanged from original     */}
      {/* ----------------------------------------------------------------- */}
      <div className="hidden lg:block w-full rounded-xl border border-border bg-card overflow-hidden card-shadow-lg select-none">
        {/* Preview header bar */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3 bg-muted/30">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground text-xs font-bold flex-shrink-0">
              SB
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                Muster Drive Systems GmbH
              </p>
              <p className="text-[10px] text-muted-foreground">
                Erstellt: heute
              </p>
            </div>
          </div>
          <div className="flex items-center rounded-md border border-border p-0.5 flex-shrink-0">
            <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-accent text-accent-foreground">
              <LayoutGrid className="h-3 w-3" />
            </div>
            <div className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground">
              <FileText className="h-3 w-3" />
            </div>
          </div>
        </div>

        {/* Mini tab row */}
        <div className="flex gap-1 px-4 py-2 border-b border-border overflow-hidden">
          {['Unternehmensprofil', 'Finanzkennzahlen', 'Versicherungsanalyse', 'Risiko-Recherche', 'Cross-Selling'].map(
            (tab, i) => (
              <span
                key={tab}
                className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${
                  i === 3
                    ? 'bg-accent/15 text-accent font-medium'
                    : 'text-muted-foreground'
                }`}
              >
                {tab}
              </span>
            )
          )}
        </div>

        {/* Section cards */}
        <div className="p-4 space-y-3">
          {/* Card 1 — Unternehmensprofil snippet */}
          <div className="rounded-lg border border-border p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent text-accent-foreground text-[8px] font-bold">
                2
              </span>
              <span className="text-xs font-semibold text-foreground">
                Unternehmensprofil
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
              Automotive Tier-1-Zulieferer, gegründet 1987 in Böblingen. Spezialisiert auf Brems- und Antriebssteuergeräte für führende OEMs.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-md bg-muted/50 px-2 py-1.5">
                <p className="text-[9px] text-muted-foreground">Branche</p>
                <p className="text-[11px] font-medium text-foreground">Automotive Tier-1</p>
              </div>
              <div className="rounded-md bg-muted/50 px-2 py-1.5">
                <p className="text-[9px] text-muted-foreground">Mitarbeiter</p>
                <p className="text-[11px] font-medium text-foreground">ca. 387</p>
              </div>
            </div>
          </div>

          {/* Card 2 — Risiko-Recherche snippet (highlighted, prioritized) */}
          <div className="rounded-lg border border-accent/30 bg-accent/5 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent text-accent-foreground text-[8px] font-bold">
                7
              </span>
              <span className="text-xs font-semibold text-foreground">
                Risiko-Recherche
              </span>
              <Badge variant="secondary" className="text-[8px] px-1.5 py-0 h-4 ml-auto">
                16 Punkte
              </Badge>
            </div>
            {/* Mini risk table */}
            <div className="space-y-1">
              <div className="grid grid-cols-3 gap-1 text-[9px] font-medium text-muted-foreground pb-1 border-b border-border/50">
                <span>Kategorie</span>
                <span>Befund</span>
                <span>Risiko</span>
              </div>
              {[
                { kategorie: 'Qualitätseskalation', befund: '⚠️ FUND', risiko: '🔴 Kritisch', risikoColor: 'text-red-600 dark:text-red-400' },
                { kategorie: 'Cyberangriff', befund: '⚠️ FUND', risiko: '🔴 Kritisch', risikoColor: 'text-red-600 dark:text-red-400' },
                { kategorie: 'OEM-Konzentration', befund: '⚠️ Strukturell', risiko: '🟠 Erhöht', risikoColor: 'text-amber-600 dark:text-amber-400' },
              ].map((row) => (
                <div key={row.kategorie} className="grid grid-cols-3 gap-1 text-[10px]">
                  <span className="text-foreground truncate">{row.kategorie}</span>
                  <span className="text-muted-foreground truncate">{row.befund}</span>
                  <span className={row.risikoColor}>{row.risiko}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fade-out gradient suggesting more content */}
        <div className="h-8 bg-gradient-to-t from-card to-transparent -mt-4 relative z-10" />
      </div>
    </>
  );
}
